import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Input,
  Spinner,
  Field,
  Subtitle1,
  Caption1,
} from '@fluentui/react-components';
import { validatePassword } from '../utils/validate-password';
import { updatePasswordByToken } from '../services/usersService';

const UserReset: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [token, setToken] = useState('');
  const [pw, setPw] = useState('');
  const [pwRepeat, setPwRepeat] = useState('');
  const [state, setState] = useState<'unsent' | 'sent'>('unsent');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    }
  }, []);

  const passwordValid = (): boolean => {
    const validate = validatePassword(pw, pwRepeat);
    setErrors(validate.errors ?? []);
    return validate.valid;
  };

  const updatePassword = async () => {
    if (!passwordValid()) {
      return;
    }
    setLoading(true);
    try {
      await updatePasswordByToken(pw, token);
      setState('sent');
      setError(false);
      setLoading(false);
      setTimeout(() => {
        navigate('/login');
      }, 1000);
    } catch (err) {
      setState('unsent');
      setError(true);
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#ebebeb]">
      <div className="flex justify-center h-[30vh] min-h-[200px] bg-gradient-to-r from-orange-700 to-yellow-500 rounded-b w-full text-white">
        <div className="text-center max-w-3xl mt-10">
          <h1 className="text-3xl">Välkommen till UPPHANDLAT</h1>
          <p className="text-sm mt-5 px-6">
            Upphandlat är ett verktyg för avrop från gemensamt upphandlade avtal.
            Verktyget tillhandahålles av Skånes Kommuner och kan användas fritt av de kommuner
            som deltar i de gemensamma upphandlingar och avtal som hanteras i verktyget,
            samt av de leverantörer vars tjänster kan avropas genom verktyget.
          </p>
        </div>
      </div>
      <div className="-mt-10">
        <div className="bg-white gap-5 flex flex-col rounded shadow-md w-[500px] m-auto overflow-y-auto p-5">
          
          <div className="flex flex-col">
            <Subtitle1>Nytt lösenord</Subtitle1>
            <Caption1>
              Ange ditt nya lösenord nedan
            </Caption1>
          </div>

          {state === 'unsent' && (
            <form className="flex flex-col gap-4">
              <Field label="Ange nytt lösenord">
                <Input
                  type="password"
                  placeholder="**********"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                />
              </Field>

              <Field label="Upprepa nytt lösenord">
                <Input
                  type="password"
                  placeholder="**********"
                  value={pwRepeat}
                  onChange={(e) => setPwRepeat(e.target.value)}
                />
              </Field>

              {errors.length > 0 && (
                <div className="flex flex-col gap-1">
                  {errors.map((err, index) => (
                    <Caption1 key={index} className="text-red-500">
                      {err}
                    </Caption1>
                  ))}
                </div>
              )}

              {error && (
                <div className="flex flex-col gap-1">
                  <Caption1 className="text-red-500">Lösenord kunde ej uppdateras!</Caption1>
                  <Caption1 className="text-red-500">
                    Tidsfristen för din begäran kan ha gått ut eller så kan server ej kontaktas.
                  </Caption1>
                </div>
              )}

              <Button
                appearance="primary"
                disabled={!pw || !pwRepeat || loading}
                onClick={updatePassword}
              >
                {loading ? "Sparar..." : "Spara"}
              </Button>
            </form>
          )}

          {state === 'sent' && (
            <div className="text-center py-4">
              <Caption1 className="text-green-600 font-semibold">Lösenord uppdaterat!</Caption1>
            </div>
          )}

          {loading && (
            <div className="flex justify-center py-4">
              <Spinner size="medium" />
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default UserReset;
